import {forwardRef, useEffect, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import ReCAPTCHA from 'react-google-recaptcha';
import CaptchaPlaceholder from './CaptchaPlaceholder';

interface CaptchaProps {
    onChange?: (value: string | null) => void;
    onExpired?: () => void;
    // Callers also pass e.g. `name`; it is not used.
    [key: string]: unknown;
}

/**
 * The reCAPTCHA widget. Forms mount it only after the visitor first interacts with them
 * (see useFirstInteraction), so Google's scripts never load with the page.
 *
 * Until Google's widget has actually rendered, the placeholder keeps its space and the widget
 * loads on top of it (absolutely positioned) — then they swap. The form below never jumps.
 * The ref exposes the ReCAPTCHA instance (e.g. `reset()`).
 */
const Captcha = forwardRef<ReCAPTCHA, CaptchaProps>(function Captcha({onChange, onExpired}, ref) {
    const boxRef = useRef<HTMLDivElement>(null);
    const [rendered, setRendered] = useState(false);

    useEffect(() => {
        const box = boxRef.current;
        if (!box || rendered) return undefined;
        const check = () => {
            if (box.querySelector('iframe')) setRendered(true);
        };
        check();
        const observer = new MutationObserver(check);
        observer.observe(box, {childList: true, subtree: true});
        return () => observer.disconnect();
    }, [rendered]);

    return (
        <Box className="google-recaptcha" ref={boxRef} style={{position: 'relative'}}>
            {!rendered && <CaptchaPlaceholder />}
            <ReCAPTCHA
                ref={ref}
                className="captcha-input"
                sitekey={`${process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY}`}
                onChange={onChange}
                onExpired={onExpired}
                style={rendered ? undefined : {position: 'absolute', top: 0, left: 0}}
            />
        </Box>
    );
});

export default Captcha;
